import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Truck, Plus, Edit3, Trash2, Mail, Phone, Save, ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";

const emptySupplier = {
  name: "",
  email: "",
  contact_name: "",
  phone: "",
  address: "",
  notes: "",
};

export default function Suppliers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ open: false, supplier: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const open = (supplier) => setForm({ open: true, supplier: { ...emptySupplier, ...(supplier || {}) } });
  const close = () => setForm({ open: false, supplier: null });

  const save = async () => {
    const s = form.supplier;
    if (!s.name?.trim()) {
      toast.error("Name ist Pflicht.");
      return;
    }
    try {
      if (s.id) {
        await api.put(`/suppliers/${s.id}`, s);
        toast.success("Lieferant aktualisiert.");
      } else {
        await api.post("/suppliers", s);
        toast.success("Lieferant angelegt.");
      }
      close();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success("Lieferant gelöscht.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const bulkReorder = async (s) => {
    if (!window.confirm(`Sammelbestellung an „${s.name}" auslösen?\n\nEs werden alle Artikel dieses Lieferanten unter Mindestmenge in EINER PDF-Bestellung per E-Mail gesendet.`)) {
      return;
    }
    try {
      const { data } = await api.post(`/suppliers/${s.id}/reorder`);
      toast.success(`Sammelbestellung verschickt: ${data.items_count} Artikel · ${data.order_no}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="max-w-md mx-auto bg-white border border-zinc-200 rounded-sm p-8 text-center">
        <ShieldCheck className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
        <h2 className="font-display text-xl uppercase mb-1">Kein Zugriff</h2>
        <p className="text-sm text-zinc-600">Lieferanten verwalten ist nur für Admins.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="suppliers-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
            LIEFERANTEN · {items.length}
          </div>
          <h1 className="font-display text-4xl uppercase flex items-center gap-3">
            <Truck className="w-8 h-8" /> Lieferanten
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Lieferanten verwalten und Artikeln zuordnen. Bei aktiviertem Auto-Reorder werden bei
            Mindestmenge automatisch PDF-Bestellungen an den Lieferanten verschickt.
          </p>
        </div>
        <Button
          onClick={() => open(null)}
          data-testid="new-supplier-button"
          className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-11 px-5 font-semibold tracking-wide"
        >
          <Plus className="w-4 h-4 mr-2" /> NEUER LIEFERANT
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-100">
              <tr>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">LIEFERANT</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">KONTAKT</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">ADRESSE</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">AKTIONEN</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-8 mono text-xs text-zinc-500">LADEN…</td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-10">
                    <div className="text-sm text-zinc-500 mb-3">Noch keine Lieferanten.</div>
                    <Button onClick={() => open(null)} className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm">
                      <Plus className="w-4 h-4 mr-1" /> Ersten Lieferanten anlegen
                    </Button>
                  </td>
                </tr>
              ) : items.map((s) => (
                <tr key={s.id} className="border-t border-zinc-100" data-testid={`supplier-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.name}</div>
                    {s.contact_name && (
                      <div className="text-xs text-zinc-500">z. Hd. {s.contact_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {s.email && (
                      <div className="flex items-center gap-1 text-xs mono">
                        <Mail className="w-3 h-3 text-zinc-400" /> {s.email}
                      </div>
                    )}
                    {s.phone && (
                      <div className="flex items-center gap-1 text-xs mono">
                        <Phone className="w-3 h-3 text-zinc-400" /> {s.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-pre-line max-w-xs">{s.address || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => bulkReorder(s)}
                        data-testid={`bulk-reorder-${s.id}`}
                        className="p-2 hover:bg-orange-100 hover:text-[#F97316] rounded-sm"
                        title="Sammelbestellung für alle Low-Stock Artikel dieses Lieferanten"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => open(s)}
                        data-testid={`edit-supplier-${s.id}`}
                        className="p-2 hover:bg-zinc-100 rounded-sm"
                        title="Bearbeiten"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            data-testid={`delete-supplier-${s.id}`}
                            className="p-2 hover:bg-red-100 hover:text-red-600 rounded-sm"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-sm">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Lieferant löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              „{s.name}" wird entfernt. Bei zugeordneten Artikeln wird die Verknüpfung gelöscht.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(s.id)}
                              className="bg-red-600 hover:bg-red-700 rounded-sm"
                            >
                              Löschen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={form.open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle>{form.supplier?.id ? "Lieferant bearbeiten" : "Neuer Lieferant"}</DialogTitle>
            <DialogDescription>
              Pflichtfeld: Name. Für Auto-Bestellung wird die E-Mail benötigt.
            </DialogDescription>
          </DialogHeader>
          {form.supplier && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label>
                <Input
                  value={form.supplier.name}
                  onChange={(e) => setForm((s) => ({ ...s, supplier: { ...s.supplier, name: e.target.value } }))}
                  data-testid="supplier-name-input"
                  className="mt-1.5 rounded-sm border-zinc-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">E-Mail</Label>
                  <Input
                    type="email"
                    value={form.supplier.email || ""}
                    onChange={(e) => setForm((s) => ({ ...s, supplier: { ...s.supplier, email: e.target.value } }))}
                    data-testid="supplier-email-input"
                    className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Telefon</Label>
                  <Input
                    value={form.supplier.phone || ""}
                    onChange={(e) => setForm((s) => ({ ...s, supplier: { ...s.supplier, phone: e.target.value } }))}
                    className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Ansprechpartner</Label>
                <Input
                  value={form.supplier.contact_name || ""}
                  onChange={(e) => setForm((s) => ({ ...s, supplier: { ...s.supplier, contact_name: e.target.value } }))}
                  className="mt-1.5 rounded-sm border-zinc-400"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Adresse</Label>
                <Textarea
                  value={form.supplier.address || ""}
                  onChange={(e) => setForm((s) => ({ ...s, supplier: { ...s.supplier, address: e.target.value } }))}
                  className="mt-1.5 rounded-sm border-zinc-400"
                  placeholder="Straße Hausnr.&#10;PLZ Ort"
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Notizen</Label>
                <Textarea
                  value={form.supplier.notes || ""}
                  onChange={(e) => setForm((s) => ({ ...s, supplier: { ...s.supplier, notes: e.target.value } }))}
                  className="mt-1.5 rounded-sm border-zinc-400"
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close} className="rounded-sm">Abbrechen</Button>
            <Button
              onClick={save}
              data-testid="save-supplier-button"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm"
            >
              <Save className="w-4 h-4 mr-2" /> Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
